import { useState, useEffect } from 'react'
import { tablesAPI } from '../../services/api'
import { 
  PlusIcon, 
  PencilIcon, 
  TrashIcon, 
  QrCodeIcon,
  PrinterIcon,
  EyeIcon,
  LinkIcon,
  ClipboardDocumentIcon
} from '@heroicons/react/24/outline'
import TableForm from '../../components/TableForm'
import toast from 'react-hot-toast'
import { t } from '../../i18n'
import { useAdminBusiness } from '../../contexts/AdminBusinessContext'

function TableManagementPage() {
  const { branch } = useAdminBusiness()
  const [tables, setTables] = useState([])
  const [loading, setLoading] = useState(true)
  const [showTableForm, setShowTableForm] = useState(false)
  const [editingTable, setEditingTable] = useState(null)
  const [showQRModal, setShowQRModal] = useState(false)
  const [selectedTable, setSelectedTable] = useState(null)
  const [qrCodeUrl, setQrCodeUrl] = useState('')

  useEffect(() => {
    loadTables()
  }, [])

  const loadTables = async () => {
    try {
      setLoading(true)
      const response = await tablesAPI.getTables()
      if (response.data.success) {
        setTables(response.data.tables)
      }
    } catch (error) {
      toast.error(t("Failed to load tables"))
      console.error('Tables load error:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleAddTable = () => {
    setEditingTable(null)
    setShowTableForm(true)
  }

  const handleEditTable = (table) => {
    setEditingTable(table)
    setShowTableForm(true)
  }

  const handleSaveTable = async (savedTable) => {
    // Reload all tables to get fresh data with activeOrder fields
    await loadTables()
    setShowTableForm(false)
    setEditingTable(null)
  }

  const handleCancelForm = () => {
    setShowTableForm(false)
    setEditingTable(null)
  }

  const handleDeleteTable = async (tableId) => {
    if (!window.confirm(t("Are you sure you want to delete this table? This action cannot be undone."))) {
      return
    }

    try {
      await tablesAPI.deleteTable(tableId)
      setTables(prev => prev.filter(table => table.id !== tableId))
      toast.success(t("Table deleted successfully"))
    } catch (error) {
      console.error('Table deletion error:', error)
      toast.error(t(error.response?.data?.error || 'Failed to delete table'))
    }
  }

  const handleShowQR = async (table) => {
    try {
      const response = await tablesAPI.getTableQR(table.id, 'dataurl')
      if (response.data.success) {
        setQrCodeUrl(response.data.qrCodeUrl)
        setSelectedTable(table)
        setShowQRModal(true)
      }
    } catch (error) {
      console.error('QR generation error:', error)
      toast.error(t("Failed to generate QR code"))
    }
  }

  const handlePrintQR = async (table) => {
    try {
      const response = await tablesAPI.getTableQR(table.id, 'dataurl')
      if (response.data.success) {
        // Open QR code in new window for printing
        const printWindow = window.open('', '_blank')
        printWindow.document.write(`
          <html>
            <head>
              <title>Table ${table.table_number} QR Code</title>
              <style>
                body { 
                  font-family: Arial, sans-serif; 
                  text-align: center; 
                  padding: 20px; 
                }
                .qr-container { 
                  margin: 20px auto; 
                  max-width: 300px; 
                }
                .table-info { 
                  margin: 20px 0; 
                  font-size: 18px; 
                }
                img { 
                  max-width: 100%; 
                  height: auto; 
                }
                @media print {
                  body { margin: 0; }
                  .no-print { display: none; }
                }
              </style>
            </head>
            <body>
              <div class="table-info">
                <h1>Table ${table.table_number}</h1>
                <p>Capacity: ${table.capacity} people</p>
                <p>Location: ${table.location || 'Main Hall'}</p>
              </div>
              <div class="qr-container">
                <img src="${response.data.qrCodeUrl}" alt="Table ${table.table_number} QR Code" />
              </div>
              <div class="no-print">
                <button onclick="window.print()">Print QR Code</button>
                <button onclick="window.close()">Close</button>
              </div>
            </body>
          </html>
        `)
        printWindow.document.close()
      }
    } catch (error) {
      console.error('Print QR error:', error)
      toast.error(t("Failed to print QR code"))
    }
  }

  const handleCopyLink = async (table) => {
    try {
      const tableUrl = branch?.website_url
        ? `${branch.website_url}/menu?table=${encodeURIComponent(table.table_number)}`
        : `${window.location.origin}/menu?table=${table.table_number}&branch=${table.branch_id}`
      await navigator.clipboard.writeText(tableUrl)
      toast.success(t("Table link copied to clipboard!"))
    } catch (error) {
      console.error('Copy link error:', error)
      toast.error(t("Failed to copy link"))
    }
  }

  const getTableStatus = (table) => {
    // Check if table has active orders
    const hasActiveOrder = table.activeOrder !== null && table.activeOrder !== undefined
    return hasActiveOrder ? 'busy' : 'free'
  }

  const getTableStatusColor = (status) => {
    switch (status) {
      case 'busy':
        return 'bg-red-100 text-red-800 border-red-200'
      case 'free':
        return 'bg-green-100 text-green-800 border-green-200'
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200'
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="loading-spinner mb-4"></div>
          <p className="text-gray-600">{t("Loading tables...")}</p>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold gradient-text">{t("Table Management")}</h1>
          <p className="text-gray-600 mt-2">{t("Manage restaurant tables and QR codes")}</p>
        </div>
        <button
          onClick={handleAddTable}
          className="btn-primary relative flex items-center justify-center h-10 px-4 rounded-md"
        >
          <PlusIcon className="h-5 w-5 me-2" />
          {t("Add Table")}
        </button>
      </div>

      {/* Tables Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
        {tables.map((table) => {
          const status = getTableStatus(table)
          return (
            <div key={table.id} className="card hover:shadow-lg transition-shadow duration-200">
              <div className="card-body">
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">{t("Table")} {table.table_number}</h3>
                    <p className="text-sm text-gray-600">{t("Capacity:")} {table.capacity} {t("people")}</p>
                    {table.location && (
                      <p className="text-sm text-gray-500">{table.location}</p>
                    )}
                  </div>
                  <span className={`badge ${getTableStatusColor(status)}`}>
                    {status === 'busy' ? t("Busy") : t("Free")}
                  </span>
                </div>

                <div className="space-y-2">
                  <button
                    onClick={() => handleShowQR(table)}
                    className="w-full btn-outline btn-sm relative flex items-center justify-center h-10 px-4 rounded-md"
                  >
                    <EyeIcon className="h-4 w-4 me-2" />
                    {t("View QR Code")}
                  </button>
                  
                  <button
                    onClick={() => handlePrintQR(table)}
                    className="w-full btn-secondary btn-sm relative flex items-center justify-center h-10 px-4 rounded-md"
                  >
                    <PrinterIcon className="h-4 w-4 me-2" />
                    {t("Print QR")}
                  </button>
                  
                  <div className="flex space-x-2">
                    <button
                      onClick={() => handleEditTable(table)}
                      className="flex-1 btn-outline btn-sm"
                    >
                      <PencilIcon className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => handleDeleteTable(table.id)}
                      className="flex-1 btn-danger btn-sm"
                    >
                      <TrashIcon className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )
        })}
      </div>

      {/* Empty State */}
      {tables.length === 0 && (
        <div className="text-center py-12">
          <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <QrCodeIcon className="h-8 w-8 text-gray-400" />
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">{t("No tables found")}</h3>
          <p className="text-gray-600 mb-6">{t("Get started by adding your first table")}</p>
          <button
            onClick={handleAddTable}
            className="btn-primary"
          >
            <PlusIcon className="h-5 w-5 me-2" />
            {t("Add First Table")}
          </button>
        </div>
      )}

      {/* Table Form Modal */}
      {showTableForm && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-xl font-semibold text-gray-900">
                  {editingTable ? t("Edit Table") : t("Add New Table")}
                </h2>
                <button
                  onClick={handleCancelForm}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <span className="sr-only">{t("Close")}</span>
                  <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <TableForm
                table={editingTable}
                onSave={handleSaveTable}
                onCancel={handleCancelForm}
                branchId={1}
              />
            </div>
          </div>
        </div>
      )}

      {/* QR Code Modal */}
      {showQRModal && selectedTable && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-md w-full">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-xl font-semibold text-gray-900">
                  {t("Table")} {selectedTable.table_number} {t("QR Code")}
                </h2>
                <button
                  onClick={() => setShowQRModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <span className="sr-only">{t("Close")}</span>
                  <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <div className="text-center space-y-4">
                <div className="bg-white border-2 border-gray-200 rounded-xl p-6">
                  <img
                    src={qrCodeUrl}
                    alt={t("Table {table_number} QR Code", { table_number: selectedTable.table_number })}
                    className="mx-auto"
                  />
                </div>
                
                <div className="text-sm text-gray-600">
                  <p>{t("Customers can scan this QR code to view the menu")}</p>
                  <p>{t("and place orders for Table")} {selectedTable.table_number}</p>
                </div>

                <div className="space-y-3">
                  <div className="flex space-x-3">
                    <button
                      onClick={() => handlePrintQR(selectedTable)}
                      className="flex-1 btn-primary relative flex items-center justify-center h-10 px-4 rounded-md"
                    >
                      <PrinterIcon className="h-5 w-5 me-2" />
                      {t("Print QR Code")}
                    </button>
                    <button
                      onClick={() => handleCopyLink(selectedTable)}
                      className="flex-1 btn-secondary relative flex items-center justify-center h-10 px-4 rounded-md"
                    >
                      <ClipboardDocumentIcon className="h-5 w-5 me-2" />
                      {t("Copy Link")}
                    </button>
                  </div>
                  <button
                    onClick={() => setShowQRModal(false)}
                    className="w-full btn-outline relative flex items-center justify-center h-10 px-4 rounded-md"
                  >
                    {t("Close")}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default TableManagementPage